import * as React from 'react'
import { Dialog, Tooltip } from '@mui/material'
import {
  CloseRounded, ChevronLeftRounded, ChevronRightRounded, DownloadRounded, PictureAsPdfOutlined, InsertDriveFileOutlined,
} from '@mui/icons-material'
import { Attachment } from '../../store/support/reducer'
import { downloadBlob, getFileExtensionFromUrl, getS3Blob } from '../../store/support/service'
import s from './SupportV2.module.css'

/*
 * Attachments in the v2 support desk: a strip of small squares (the image
 * itself, or a PDF / file mark), the name on hover, and a viewer on click —
 * the full image or the PDF, ← → through the ticket's files, Download, Esc.
 * Files come the way the classic FilePreview gets them (getS3Blob, with the
 * admin's token); one not sent yet (isObjectUrl) is already in the browser.
 * Each file is fetched once and kept for the session.
 */

const blobs = new Map<string, Promise<Blob>>()
const urls = new Map<string, string>()

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'tiff', 'webp', 'bmp']
const EXT_RE = /^[a-z0-9]{2,5}$/

/*
 * The type comes from the original file name: chat files are stored under a
 * key with no extension (`<uuid>-comment_attachment`), so the URL can't tell.
 * The URL is only a fallback; an unknown type is shown as an image, as the
 * classic FilePreview does.
 */
const extOf = (a: Attachment) => {
  const fromName = (getFileExtensionFromUrl(a.fileName || '') || '').toLowerCase()
  if (EXT_RE.test(fromName) && fromName !== (a.fileName || '').toLowerCase()) return fromName
  const fromUrl = (getFileExtensionFromUrl(a.url) || '').toLowerCase()
  return EXT_RE.test(fromUrl) ? fromUrl : ''
}
const kindOf = (a: Attachment): 'image' | 'pdf' | 'file' => {
  const ext = extOf(a)
  if (ext === 'pdf') return 'pdf'
  return !ext || IMAGE_EXT.includes(ext) ? 'image' : 'file'
}
const mimeOf = (a: Attachment) => {
  const ext = extOf(a)
  if (ext === 'pdf') return 'application/pdf'
  if (ext === 'jpg') return 'image/jpeg'
  return IMAGE_EXT.includes(ext) ? `image/${ext}` : ''
}

function blobOf(a: Attachment): Promise<Blob> {
  if (a.isObjectUrl) return fetch(a.url).then((r) => r.blob())
  let p = blobs.get(a.url)
  if (!p) {
    // getS3Blob types the file by its URL, which a chat file's lacks — retype it by its name
    p = getS3Blob(a.url).then((b) => {
      const type = mimeOf(a)
      return type && b.type !== type ? new Blob([b], { type }) : b
    })
    p.catch(() => blobs.delete(a.url))
    blobs.set(a.url, p)
  }
  return p
}

/** an object URL for the file, or null while it loads / when it failed */
function useFileUrl(a: Attachment | null): { url: string | null; failed: boolean } {
  const [state, setState] = React.useState<{ url: string | null; failed: boolean }>({ url: null, failed: false })
  React.useEffect(() => {
    if (!a) return undefined
    if (a.isObjectUrl) { setState({ url: a.url, failed: false }); return undefined }
    const cached = urls.get(a.url)
    if (cached) { setState({ url: cached, failed: false }); return undefined }
    let alive = true
    setState({ url: null, failed: false })
    blobOf(a)
      .then((blob) => {
        const u = urls.get(a.url) || URL.createObjectURL(blob)
        urls.set(a.url, u)
        if (alive) setState({ url: u, failed: false })
      })
      .catch(() => { if (alive) setState({ url: null, failed: true }) })
    return () => { alive = false }
  }, [a])
  return state
}

function Thumb({ a, size, onOpen, onRemove }: { a: Attachment; size: number; onOpen: () => void; onRemove?: () => void }) {
  const kind = kindOf(a)
  const { url, failed } = useFileUrl(kind === 'image' ? a : null)
  return (
    <Tooltip title={a.fileName}>
      <span className={s.thumbWrap} style={{ width: size, height: size }}>
        <button type="button" className={s.thumb} onClick={onOpen} aria-label={`Open ${a.fileName}`}>
          {kind === 'image' && url && <img src={url} alt="" />}
          {kind === 'image' && !url && <span className={s.thumbMark}>{failed ? '!' : ''}</span>}
          {kind === 'pdf' && <span className={s.thumbMark}><PictureAsPdfOutlined sx={{ fontSize: size > 44 ? 22 : 17 }} /><small>PDF</small></span>}
          {kind === 'file' && <span className={s.thumbMark}><InsertDriveFileOutlined sx={{ fontSize: size > 44 ? 22 : 17 }} /><small>{extOf(a).toUpperCase() || 'FILE'}</small></span>}
        </button>
        {onRemove && (
          <button type="button" className={s.thumbRemove} onClick={onRemove} aria-label={`Remove ${a.fileName}`}><CloseRounded sx={{ fontSize: 12 }} /></button>
        )}
      </span>
    </Tooltip>
  )
}

function Viewer({ items, start, onClose }: { items: Attachment[]; start: number; onClose: () => void }) {
  const [i, setI] = React.useState(start)
  const a = items[i]
  const kind = kindOf(a)
  const { url, failed } = useFileUrl(kind === 'file' ? null : a)
  const go = React.useCallback((d: number) => setI((x) => (x + d + items.length) % items.length), [items.length])

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && items.length > 1) go(-1)
      if (e.key === 'ArrowRight' && items.length > 1) go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, items.length])

  async function download() {
    try { downloadBlob(await blobOf(a), a.fileName) } catch { /* the api layer reports it */ }
  }

  return (
    <Dialog open onClose={onClose} fullScreen PaperProps={{ sx: { background: 'rgba(8, 10, 22, .92)', boxShadow: 'none' } }}>
      <div className={s.viewer}>
        <div className={s.viewerTop}>
          <span className={s.viewerName}>{a.fileName}{items.length > 1 ? ` · ${i + 1} of ${items.length}` : ''}</span>
          <button type="button" className={s.viewerBtn} onClick={() => { void download() }}><DownloadRounded sx={{ fontSize: 17 }} />Download</button>
          <button type="button" className={s.viewerIcon} onClick={onClose} aria-label="Close"><CloseRounded sx={{ fontSize: 20 }} /></button>
        </div>
        <div className={s.viewerStage}>
          {items.length > 1 && <button type="button" className={s.viewerNav} onClick={() => go(-1)} aria-label="Previous"><ChevronLeftRounded sx={{ fontSize: 26 }} /></button>}
          <div className={s.viewerBody}>
            {kind === 'image' && url && <img src={url} alt={a.fileName} />}
            {kind === 'pdf' && url && <iframe src={url} title={a.fileName} />}
            {kind === 'file' && <span className={s.viewerNote}>No preview for this file — download it.</span>}
            {kind !== 'file' && !url && <span className={s.viewerNote}>{failed ? 'The file could not be loaded.' : 'Loading…'}</span>}
          </div>
          {items.length > 1 && <button type="button" className={s.viewerNav} onClick={() => go(1)} aria-label="Next"><ChevronRightRounded sx={{ fontSize: 26 }} /></button>}
        </div>
      </div>
    </Dialog>
  )
}

export function AttachmentsV2({ items, size = 56, label, onRemove }: {
  items: Attachment[]
  size?: number
  /** "3 attachments" after the strip */
  label?: boolean
  onRemove?: (a: Attachment) => void
}) {
  const [open, setOpen] = React.useState<number | null>(null)
  if (!items.length) return null
  return (
    <>
      <div className={s.thumbs}>
        {items.map((a, i) => (
          <Thumb key={a.url} a={a} size={size} onOpen={() => setOpen(i)} onRemove={onRemove ? () => onRemove(a) : undefined} />
        ))}
        {label && <span className={s.thumbsLabel}>{items.length} {items.length === 1 ? 'attachment' : 'attachments'}</span>}
      </div>
      {open !== null && <Viewer items={items} start={open} onClose={() => setOpen(null)} />}
    </>
  )
}
